import React, { useMemo } from "react";
import { Box, FormControl, FormLabel, MenuItem, Select } from "@mui/material";
import HPInput from "@components/common/Input";
import HPDropdown from "@components/common/Dropdown";

const ALLOWED_CHARS = /^[a-zA-Z0-9 _\-]*$/;
const MAX_NAME_LENGTH = 100;

interface RollupBuilderProps {
  focusMode: "variable" | "value";
  allVariables: any[];
  allValues: any[];
  rollupName: string;
  rollupSelectedLabels: string[];
  onRollupNameChange: (name: string) => void;
  onSelectedLabelsChange: (labels: string[]) => void;
}

const RollupBuilder: React.FC<RollupBuilderProps> = ({
  focusMode,
  allVariables,
  allValues,
  rollupName,
  rollupSelectedLabels,
  onRollupNameChange,
  onSelectedLabelsChange,
}) => {
  const isVariableMode = focusMode === "variable";

  const categoryOptions = isVariableMode
    ? allVariables.map((v) => ({ value: v.label, label: v.rowtitle || v.title }))
    : allValues.map((v) => ({ value: String(v.value), label: v.title || v.label }));

  const existingNames = useMemo(() => {
    const names = new Set<string>();
    const add = (s: string | undefined) => {
      if (s?.trim()) names.add(s.trim().toLowerCase());
    };
    allVariables.forEach((v: any) => { add(v.title); add(v.rowtitle); add(v.label); });
    allValues.forEach((v: any) => { add(v.title); add(v.label); });
    return names;
  }, [allVariables, allValues]);

  const getNameError = (): string | null => {
    if (!rollupName) return null;
    const trimmed = rollupName.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length > MAX_NAME_LENGTH) return `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
    if (!ALLOWED_CHARS.test(trimmed)) return "Use letters, numbers, spaces, hyphens, or underscores only";
    if (existingNames.has(trimmed.toLowerCase())) return "A category with this name already exists. Use a unique rollup name.";
    return null;
  };

  const nameError = getNameError();
  const selectionError =
    rollupSelectedLabels.length > 0 && rollupSelectedLabels.length < 2
      ? "At least 2 categories required"
      : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth size="small">
        <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5 }}>
          Rollup Type
        </FormLabel>
        <Select value="SUM_SELECTED" size="small">
          <MenuItem value="SUM_SELECTED">Combine selected categories</MenuItem>
        </Select>
      </FormControl>

      <HPInput
        label="Rollup Name*"
        value={rollupName}
        onChange={(e) => onRollupNameChange(e.target.value)}
        placeholder="Enter a name for this rollup"
        error={!!nameError}
        helperText={nameError ?? ""}
      />

      <HPDropdown
        label={`Source ${isVariableMode ? "Variables" : "Values"}* (select at least 2)`}
        value={rollupSelectedLabels}
        onChange={onSelectedLabelsChange}
        options={categoryOptions}
        multiSelect
        countLabel={isVariableMode ? "variables" : "values"}
        placeholder={`Choose ${isVariableMode ? "variables" : "values"}`}
        error={!!selectionError}
        helperText={selectionError ?? ""}
      />
    </Box>
  );
};

export default RollupBuilder;
